// Generate a consistent hash from the name
const hashCode = (str: string) => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32bit integer
  }
  return Math.abs(hash)
}

// Generate HSL color based on name hash
const generateColor = (name: string) => {
  const hash = hashCode(name);

  const h = hash % 360;
  const s = 65 + (hash % 20); // Saturation 65-85%
  let l = 25 + (hash % 20); // Lightness 25-44% (initially)

  // Convert HSL to RGB
  const hslToRgb = (h: number, s: number, l: number) => {
    s /= 100;
    l /= 100;
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    let r, g, b;

    if (h >= 0 && h < 60) {
      [r, g, b] = [c, x, 0];
    } else if (h < 120) {
      [r, g, b] = [x, c, 0];
    } else if (h < 180) {
      [r, g, b] = [0, c, x];
    } else if (h < 240) {
      [r, g, b] = [0, x, c];
    } else if (h < 300) {
      [r, g, b] = [x, 0, c];
    } else {
      [r, g, b] = [c, 0, x];
    }

    return {
      r: Math.round((r + m) * 255),
      g: Math.round((g + m) * 255),
      b: Math.round((b + m) * 255),
    };
  };

  // Calculate relative luminance for contrast ratio
  const getRelativeLuminance = (r: number, g: number, b: number) => {
    const rs = r / 255;
    const gs = g / 255;
    const bs = b / 255;

    const adjust = (c: number) =>
      c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);

    return 0.2126 * adjust(rs) + 0.7152 * adjust(gs) + 0.0722 * adjust(bs);
  };

  let rgb = hslToRgb(h, s, l);
  let luminance = getRelativeLuminance(rgb.r, rgb.g, rgb.b);
  let contrast = (1.05) / (luminance + 0.05); // Contrast ratio with white (1.05 is 1 + 0.05)

  // Ensure minimum contrast ratio of 4.5:1 by reducing lightness if needed
  while (contrast < 4.5 && l > 0) {
    l--;
    rgb = hslToRgb(h, s, l);
    luminance = getRelativeLuminance(rgb.r, rgb.g, rgb.b);
    contrast = (1.05) / (luminance + 0.05);
  }

  return { h, s, l };
};

// Determine if text should be light or dark based on background lightness
const getTextColor = (lightness: number) => {
  return lightness < 60 ? "text-white" : "text-black"
}

export const gen_color = (name: string) => {
  const { h, s, l } = generateColor(name)
  const textColorClass = getTextColor(l)

  return {
    backgroundColor: `hsl(${h}, ${s}%, ${l}%)`,
    borderColor: `hsl(${h}, ${s}%, ${l - 10}%)`,
    textColorClass
  }
}